import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Utility function to get the correct name based on language
export const getTechniqueName = (technique: Technique, locale: string = 'en'): string => {
  if (locale === 'pt' && technique.name_br) {
    return technique.name_br;
  }
  return technique.name;
};

export const getCategoryName = (category: TechniqueCategory, locale: string = 'en'): string => {
  if (locale === 'pt' && category.name_br) {
    return category.name_br;
  }
  return category.name;
};

// Define the order for technique categories (must match exact names from database)
const CATEGORY_ORDER = [
  'Positions',
  'Guard Passes',
  'Guards',
  'Submissions',
  'Takedowns',
  'Defense & Escapes'
];

// Function to sort technique categories according to the defined order
export const sortTechniqueCategories = (categories: TechniqueCategory[]): TechniqueCategory[] => {
  const categoryOrderMap = new Map(CATEGORY_ORDER.map((name, index) => [name.toLowerCase(), index]));
  
  return [...categories].sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    const aIndex = categoryOrderMap.get(aName) ?? Infinity;
    const bIndex = categoryOrderMap.get(bName) ?? Infinity;
    
    // If both are in the order, sort by their order
    if (aIndex !== Infinity && bIndex !== Infinity) {
      return aIndex - bIndex;
    }
    // If only one is in the order, it comes first
    if (aIndex !== Infinity) return -1;
    if (bIndex !== Infinity) return 1;
    // If neither is in the order, maintain original order (alphabetical)
    return aName.localeCompare(bName);
  });
};

export interface Technique {
  id: number;
  name: string;
  category: string;
  name_br?: string;
}

export interface TechniqueCategory {
  id: number;
  name: string;
  name_br?: string;
  techniques: Technique[];
}

export const useTechniques = () => {
  return useQuery({
    queryKey: ['techniques'],
    queryFn: async (): Promise<TechniqueCategory[]> => {
      // Use supabase.functions.invoke() to automatically include auth token
      // Even though techniques are public, the Edge Runtime requires a token
      const { data: result, error } = await supabase.functions.invoke('get_techniques', {
        body: {}
      });

      if (error) {
        throw new Error(error.message || 'Failed to fetch techniques');
      }
      
      // Group techniques by category
      const categoryMap = new Map();
      result.techniques.forEach((technique: any) => {
        if (!categoryMap.has(technique.category)) {
          categoryMap.set(technique.category, {
            id: technique.id, // This will be overwritten, but we need an ID
            name: technique.category,
            name_br: technique.category_br,
            techniques: []
          });
        }
        categoryMap.get(technique.category).techniques.push({
          id: technique.id,
          name: technique.name,
          category: technique.category,
          name_br: technique.name_br
        });
      });

      const categories = Array.from(categoryMap.values());
      return sortTechniqueCategories(categories);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook for getting techniques in the old format (for backward compatibility)
export const useTechniquesLegacy = () => {
  return useQuery({
    queryKey: ['techniques-legacy'],
    queryFn: async () => {
      const { data: techniques, error } = await supabase
        .from('techniques')
        .select(`
          id,
          name,
          name_br,
          technique_categories!inner (
            id,
            name,
            name_br
          )
        `)
        .order('name');

      if (error) {
        throw new Error('Failed to fetch techniques');
      }

      // Group by category
      const grouped: Record<string, string[]> = {};
      techniques?.forEach(technique => {
        const categoryName = technique.technique_categories.name;
        if (!grouped[categoryName]) {
          grouped[categoryName] = [];
        }
        grouped[categoryName].push(technique.name);
      });

      return grouped;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
